import { Box, Grid, Typography, Button, Badge } from '@mui/material';
import ApplicantsListTable, { Applicant } from '../../components/ApplicantsListTable/ApplicantsListTable';
import { useParams } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import HttpHelper from '../../utils/HttpHelper';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import { ApplicationStatus } from '../../components/JobCard/JobStatusCard';
import CandidateFilterModal, {
  CandidateFilters,
  defaultFilters,
} from '../../components/Filter/CandidateFilterModal';

const ApplicantsList: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const [jobTitle, setJobTitle] = useState<string>('');
  const [allApplicants, setAllApplicants] = useState<Applicant[]>([]);
  const [activeFilters, setActiveFilters] = useState<CandidateFilters>(defaultFilters);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const getJobTitle = () => {
    if (!jobId) return;

    HttpHelper.get(`jobs/${jobId}`).then(response => {
      if (response.errorMessage) {
        alert(response.errorMessage);
      } else {
        setJobTitle(response.job.title);
      }
    });
  };

  useEffect(() => {
    getJobTitle();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Count how many filter fields are active (non-empty)
  const activeFilterCount = Object.values(activeFilters).filter(
    v => v !== '' && v !== null && v !== undefined,
  ).length;

  const getStatusText = (status: number) => {
    switch (status) {
      case ApplicationStatus.PENDING_REVIEW: return 'Pending Review';
      case ApplicationStatus.PENDING_DECISION: return 'Pending Decision';
      case ApplicationStatus.SHORTLISTED: return 'Shortlisted';
      case ApplicationStatus.ACCEPTED: return 'Accepted';
      case ApplicationStatus.REJECTED: return 'Rejected';
      case ApplicationStatus.INCOMPLETE: return 'Not Submitted';
      default: return 'N/A';
    }
  };

  /**
   * Returns the subset of allApplicants that pass the currently-active filters.
   * Mirrors the logic in ApplicantsListTable so the CSV reflects what is visible.
   */
  const getFilteredApplicants = useCallback((): Applicant[] => {
    const f = activeFilters;
    return allApplicants.filter(a => {
      const fullName = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
      const email = (a.email || '').toLowerCase();

      if (f.search) {
        const q = f.search.toLowerCase();
        if (!fullName.includes(q) && !email.includes(q)) return false;
      }
      if (f.status !== '' && a.status !== Number(f.status)) return false;
      if (f.minScore !== '' && (a.totalGrade ?? -Infinity) < parseFloat(f.minScore)) return false;
      if (f.maxScore !== '' && (a.totalGrade ?? Infinity) > parseFloat(f.maxScore)) return false;
      if (f.dateAppliedFrom && a.submittedAt && new Date(a.submittedAt) < new Date(f.dateAppliedFrom)) return false;
      if (f.dateAppliedTo && a.submittedAt && new Date(a.submittedAt) > new Date(f.dateAppliedTo)) return false;
      if (f.degreeLevel && !(a.degree || '').toLowerCase().includes(f.degreeLevel.toLowerCase())) return false;
      if (f.fieldOfStudy && !(a.degree || '').toLowerCase().includes(f.fieldOfStudy.toLowerCase())) return false;
      if (f.immigrationStatus && !(a.immigrationStatus || '').toLowerCase().includes(f.immigrationStatus.toLowerCase())) return false;

      const toYesNo = (val: string | null | undefined) => {
        const v = (val || '').toLowerCase();
        return v === 'yes' || v === 'true' || v === '1';
      };
      if (f.workEligibility !== '' && toYesNo(a.workEligibility) !== (f.workEligibility === 'yes')) return false;
      if (f.ageEligibility !== '' && toYesNo(a.ageEligibility) !== (f.ageEligibility === 'yes')) return false;
      if (f.availableBeforeJuly4 !== '' && toYesNo(a.availableBeforeJuly4) !== (f.availableBeforeJuly4 === 'yes')) return false;

      return true;
    });
  }, [allApplicants, activeFilters]);

  const handleDownloadCSV = () => {
    const filtered = getFilteredApplicants();
    const headers = ['Name', 'Email', 'Score', 'Status', 'GPA', 'School', 'Program', 'Immigration Status', 'Work Eligible', 'Age Eligible'];
    const rows = filtered.map(a => [
      `${a.firstName || ''} ${a.lastName || ''}`.trim(),
      a.email || 'N/A',
      a.totalGrade !== null ? a.totalGrade.toString() : 'N/A',
      getStatusText(a.status),
      a.gpa !== null && a.gpa !== undefined ? a.gpa.toString() : 'N/A',
      a.schoolName || 'N/A',
      a.degree || 'N/A',
      a.immigrationStatus || 'N/A',
      a.workEligibility || 'N/A',
      a.ageEligibility || 'N/A',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Applicants_${jobTitle.replace(/\s+/g, '_')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleApplyFilters = (filters: CandidateFilters) => {
    setActiveFilters(filters);
  };

  return (
    <Box sx={{ width: '100%', marginLeft: 'auto', marginRight: 'auto' }}>
      <Grid container spacing={0.5}>
        {/* Header row — matches Job Postings layout */}
        <Grid item xs={12} sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

            {/* Left — Filter Candidates */}
            <Badge
              badgeContent={activeFilterCount}
              color="primary"
              invisible={activeFilterCount === 0}
            >
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setFilterModalOpen(true)}
                size="small"
                sx={{
                  borderColor: activeFilterCount > 0 ? '#1976d2' : '#1976d2',
                  color: '#1976d2',
                  textTransform: 'none',
                  fontWeight: activeFilterCount > 0 ? 700 : 400,
                  backgroundColor: activeFilterCount > 0 ? 'rgba(25,118,210,0.06)' : 'transparent',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                }}
              >
                Filter Candidates
              </Button>
            </Badge>

            {/* Centre — Job title */}
            <Typography variant="h5" fontWeight="bold">
              Applicants - {jobTitle}
            </Typography>

            {/* Right — Download CSV */}
            <Button
              variant="outlined"
              startIcon={<CloudDownloadIcon />}
              onClick={handleDownloadCSV}
              disabled={allApplicants.length === 0}
              size="small"
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                textTransform: 'none',
                fontWeight: 'bold',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                },
              }}
            >
              Download CSV
            </Button>
          </Box>
        </Grid>

        {/* Table */}
        <Grid item xs={12}>
          <ApplicantsListTable
            onApplicantsFetched={setAllApplicants}
            activeFilters={activeFilters}
          />
        </Grid>
      </Grid>

      {/* Filter Modal */}
      <CandidateFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onApply={handleApplyFilters}
        activeFilterCount={activeFilterCount}
      />
    </Box>
  );
};

export default ApplicantsList;